import { X } from 'lucide-react';
import styled from 'styled-components';

export type MoveRecapChip = {
    key: string;
    // 1-based selection (click) order — shown as a badge so the planner sees the sequence being built.
    order: number;
    approId: string;
    ranks: number[];
    // Site name(s) of the regroupement and its current appro — what the planner is carrying.
    sites: string;
    appro: string;
};

type PlanningMoveRecapProps = {
    chips: MoveRecapChip[];
    onRemove: (approId: string, ranks: number[]) => void;
};

// Recap of the regroupements currently selected to move: one removable chip each (site + current appro), so the
// planner always sees what they're carrying while picking the target stop. Hidden when nothing is selected.
export const PlanningMoveRecap = ({ chips, onRemove }: PlanningMoveRecapProps) => {
    if (chips.length === 0) return null;

    return (
        <Wrapper data-test="planning-move-recap">
            <Label>À déplacer</Label>
            <Chips>
                {chips.map((chip) => (
                    <Chip key={chip.key} data-test="planning-move-recap-chip">
                        <OrderBadge data-test="planning-move-recap-order">{chip.order}</OrderBadge>
                        <ChipSites title={chip.sites}>{chip.sites}</ChipSites>
                        <ChipAppro>· {chip.appro}</ChipAppro>
                        <RemoveButton
                            type="button"
                            onClick={() => onRemove(chip.approId, chip.ranks)}
                            aria-label={`Retirer ${chip.sites}`}
                        >
                            <X size={12} />
                        </RemoveButton>
                    </Chip>
                ))}
            </Chips>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
`;

const Label = styled.span`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.infoTextColor};
    flex-shrink: 0;
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-height: 76px;
    overflow-y: auto;
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 2px 4px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 999px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    font-size: 12px;
    color: ${({ theme }) => theme.textColor};
`;

// Numéro d'ordre de sélection (1·2·3…), en pastille pleine pour matcher le badge des marqueurs carte.
const OrderBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ theme }) => theme.textColor};
    color: ${({ theme }) => theme.cardBackgroundColor};
    font-size: 11px;
    font-weight: 700;
`;

const ChipSites = styled.span`
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ChipAppro = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    white-space: nowrap;
`;

const RemoveButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 2px;
    border-radius: 50%;
    cursor: pointer;
    color: ${({ theme }) => theme.infoTextColor};

    &:hover {
        color: ${({ theme }) => theme.errorColor};
        background: ${({ theme }) => `color-mix(in srgb, ${theme.errorColor} 14%, transparent)`};
    }
`;
